﻿import { Select, Slider, Switch } from 'antd';
import InputNumberExt from 'msg-ui-react/dist/components/data-entry/InputNumberExt';
import { DefaultOptionType } from "rc-select/lib/Select";
import React from 'react';
import { ComponentConfigsMap } from '../forms/ComponentsMap';
import { ChartType, IBathymetryState, IBathymetryStateOptions } from '../models/ChartModels';
import { IColorScale } from '../models/ColorScale';
import { colorScales as colorScalesModels, ColorScaleSpaceIds } from '../models/ColorScales';
import { BathymetryStore } from '../stores/BathymetryStore';
import { style, switchProps } from './styles';

/**
 * Describes components used to create a UI for Bathymetry
 */
export const bathymetryConfig = (bathymetryStore: BathymetryStore): ComponentConfigsMap<IBathymetryState, IBathymetryStateOptions> => {
    return {
        spaceId: {
            hidden: (state, optional) => optional?.visibility?.bathymetryProperties?.colorScale === false,
            label: "Color scale",
            component: (state, optional) => {
                let options: DefaultOptionType[] | undefined;          

                if (state.colorScales) {
                    const scales = getColorScales(state.colorScales, state.spaceId, optional?.type);
                    const toOption = (colorScale: IColorScale) => ({
                        label: colorScale.name,
                        value: colorScale.spaceId,
                        title: '',
                        disabled: !optional?.visibility?.bathymetryProperties?.disabledColorScaleSpaceIds ? false :
                            (optional?.visibility?.bathymetryProperties?.disabledColorScaleSpaceIds.findIndex(id => colorScale.spaceId == id) > -1 ? true : false)
                    });

                    if (state.colorScales === colorScalesModels) {
                        options = scales.map(toOption);
                    }
                    else {
                        options = [{
                            label: 'Made Smart Group',
                            options: scales.filter(cs => cs.displayCategory === 0)?.map(toOption),
                        }];

                        if (scales.filter(cs => cs.displayCategory === 2).length > 0)
                            options.push({
                                label: 'Recommended',
                                options: scales.filter(cs => cs.displayCategory === 2)?.map(toOption),
                            });

                        if (scales.filter(cs => cs.displayCategory === 10).length > 0)
                            options.push({
                                label: 'Other',
                                options: scales.filter(cs => cs.displayCategory === 10)?.map(toOption),
                            })
                    }
                }

                return (
                    <Select
                        style={{
                            whiteSpace: "nowrap",
                            ...style
                        }}
                        options={options}
                    />                        
                );                
            }
        },
        colorScaleLegend: {
            hidden: (state, optional) => bathymetryVisibility(optional)?.colorScale === false || bathymetryVisibility(optional)?.colorScaleLegend === false,
            label: 'Legend',
            component: <Switch {...switchProps} />
        },
        depthShallow: {
            label: "Shallow",
            hidden: (state, optional) => {
                if (state.colorScales?.find(cs => cs.spaceId === state.spaceId)?.hasFixedValues)
                    return true;

                const visible = optional?.visibility?.bathymetryProperties?.depthShallow;
                if (visible !== undefined)
                    return !visible;
                else
                    return state.spaceId === undefined;
            },
            component: state => <InputNumberExt
                 max={state.spaceId === -1 || state.spaceId === -2 ? -1 : undefined} addonAfter='m' status={state.depthDeep >= state.depthShallow ? 'error' : undefined}
            />
        },
        depthDeep: {
            label: "Deep",
            hidden: (state, optional) => {
                if (state.colorScales?.find(cs => cs.spaceId === state.spaceId)?.hasFixedValues) {
                    return true;
                }

                const visible = optional?.visibility?.bathymetryProperties?.depthShallow;
                if (visible !== undefined)
                    return !visible;
                else
                    return state.spaceId === undefined;
            },
            component: state => <InputNumberExt
                addonAfter='m' status={state.depthDeep >= state.depthShallow ? 'error' : undefined}
            />
        },
        hillShading: {
            hidden: (state, optional) => {
                const visible = optional?.visibility?.bathymetryProperties?.hillShading;
                if (visible !== undefined)
                    return !visible;
                else
                    return state.spaceId === undefined;
            },
            component: <Switch {...switchProps} />
        },
        terrainExaggeration: {
            label: 'Exaggeration',
            hidden: (state, optional) => {
                const visible = optional?.visibility?.bathymetryProperties?.terrainExaggeration;
                if (visible !== undefined)
                    return !visible;
                else
                    return state.spaceId === undefined || !state.hillShading;
            },
            component: <Slider
                min={1.0} max={50} step={0.5}
                tooltip={{
                    formatter: value => `${value}`
                }}
            />
        }
    }
}

const bathymetryVisibility = (optional: IBathymetryStateOptions | undefined) => {
    return optional?.visibility?.bathymetryProperties;
}

const getColorScales = (colorScales: IColorScale[], spaceId?: number, type?: ChartType) => {
    if (type === ChartType.ECDIS && spaceId !== ColorScaleSpaceIds.Paper)
        return colorScales.filter(cs => cs.spaceId !== ColorScaleSpaceIds.Paper);
    else if (type === ChartType.Paper && spaceId !== ColorScaleSpaceIds.ECDIS)
        return colorScales.filter(cs => cs.spaceId !== ColorScaleSpaceIds.ECDIS);
    else
        return colorScales;
}